import { useState } from "react";
import { ExternalLink, Loader2, Home, BedDouble, Bath, Maximize, CalendarDays, Image, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

export const buildZillowUrl = (address) => {
  if (!address) return "";
  const cleaned = address.replace(/[^a-zA-Z0-9\s,]/g, "").replace(/\s+/g, "-");
  return `https://www.zillow.com/homes/${encodeURIComponent(cleaned)}_rb/`;
};

export const ZillowLinkCard = ({ transaction, onSave, onUpdate }) => {
  const [url, setUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);

  const currentUrl = transaction.zillow_url || "";
  const data = transaction.zillow_data || {};
  const hasData = Object.keys(data).length > 0 && (data.price || data.bedrooms || data.sqft || data.title || data.meta_description);

  const startEdit = () => {
    setUrl(currentUrl || buildZillowUrl(transaction.property_address));
    setEditing(true);
  };

  const handleFetchData = async () => {
    if (!url.trim() || !url.includes("zillow.com")) {
      toast.error("Please enter a valid Zillow URL");
      return;
    }
    setFetching(true);
    try {
      const res = await axios.post(
        `${API}/transactions/${transaction.transaction_id}/zillow-fetch`,
        { url: url.trim() },
        { withCredentials: true }
      );
      toast.success("Zillow listing data fetched");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch Zillow data");
    } finally {
      setFetching(false);
    }
  };

  const handleSaveUrlOnly = () => {
    if (onSave) onSave(url.trim());
    setEditing(false);
  };

  const handleGetLink = async () => {
    setSearching(true);
    try {
      const res = await axios.post(
        `${API}/transactions/${transaction.transaction_id}/zillow-search`,
        {},
        { withCredentials: true }
      );
      const foundUrl = res.data.zillow_url;
      if (foundUrl) {
        setUrl(foundUrl);
        setEditing(true);
        toast.success(
          res.data.source === "redirect" ? "Found exact listing!" :
          res.data.source === "search_result" ? "Found a matching listing" :
          "Generated search link — verify this is the right listing"
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to search Zillow");
    } finally {
      setSearching(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(price);
  };

  const photos = (data.photos || []).filter(Boolean);

  return (
    <Card className="card-surface" data-testid="zillow-link-card">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider flex items-center gap-2">
            <Home className="w-4 h-4" /> Zillow Listing
          </CardTitle>
          {!editing && (
            <div className="flex items-center gap-1">
              {!currentUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGetLink}
                  disabled={searching}
                  className="text-[var(--t-primary)] hover:text-[var(--t-primary-hover)] h-7 text-xs"
                  data-testid="get-zillow-link-btn"
                >
                  {searching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                  {searching ? "Searching..." : "Get Link"}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={startEdit}
                className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)] h-7 text-xs"
                data-testid="edit-zillow-btn"
              >
                {currentUrl ? "Update" : "Add URL"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {editing ? (
          <div className="space-y-3">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste Zillow listing URL here..."
              className="h-8 text-xs font-mono"
              data-testid="zillow-url-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-zillow-edit">
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveUrlOnly}
                data-testid="save-zillow-link-only"
              >
                Save Link
              </Button>
              <Button
                size="sm"
                onClick={handleFetchData}
                disabled={fetching}
                className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]"
                data-testid="fetch-zillow-data-btn"
              >
                {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                {fetching ? "Fetching..." : "Fetch Listing Data"}
              </Button>
            </div>
          </div>
        ) : hasData ? (
          <div className="space-y-3">
            {/* Price */}
            {data.price && (
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)]">{formatPrice(data.price)}</span>
                {data.zestimate && data.zestimate !== data.price && (
                  <span className="text-xs text-[var(--t-text-muted)]">Zestimate: {formatPrice(data.zestimate)}</span>
                )}
              </div>
            )}

            {/* Key stats */}
            <div className="flex flex-wrap gap-3 text-sm">
              {data.bedrooms && (
                <span className="flex items-center gap-1 text-[var(--t-text-muted)]" data-testid="zillow-beds">
                  <BedDouble className="w-3.5 h-3.5" /> {data.bedrooms} bed
                </span>
              )}
              {data.bathrooms && (
                <span className="flex items-center gap-1 text-[var(--t-text-muted)]" data-testid="zillow-baths">
                  <Bath className="w-3.5 h-3.5" /> {data.bathrooms} bath
                </span>
              )}
              {data.sqft && (
                <span className="flex items-center gap-1 text-[var(--t-text-muted)]" data-testid="zillow-sqft">
                  <Maximize className="w-3.5 h-3.5" /> {Number(data.sqft).toLocaleString()} sqft
                </span>
              )}
              {data.year_built && (
                <span className="flex items-center gap-1 text-[var(--t-text-muted)]" data-testid="zillow-year">
                  <CalendarDays className="w-3.5 h-3.5" /> Built {data.year_built}
                </span>
              )}
            </div>

            {data.home_type && (
              <p className="text-xs text-[var(--t-text-subtle)] capitalize">{data.home_type.replace(/_/g, " ").toLowerCase()}</p>
            )}

            {/* Photos */}
            {photos.length > 0 && (
              <div>
                <button
                  onClick={() => setShowPhotos(!showPhotos)}
                  className="flex items-center gap-1 text-xs text-[var(--t-primary)] hover:underline"
                  data-testid="toggle-zillow-photos"
                >
                  <Image className="w-3.5 h-3.5" />
                  {showPhotos ? "Hide" : "Show"} Photos ({photos.length})
                </button>
                {showPhotos && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {photos.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt={`Listing photo ${i + 1}`}
                        className="w-full h-20 object-cover rounded border border-[var(--t-border)]"
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Title / meta fallback */}
            {!data.price && data.title && (
              <p className="text-sm text-[var(--t-text)]">{data.title}</p>
            )}
            {!data.price && data.meta_description && (
              <p className="text-xs text-[var(--t-text-muted)] line-clamp-3">{data.meta_description}</p>
            )}

            {/* Link */}
            {currentUrl && (
              <a
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                data-testid="zillow-link"
              >
                <ExternalLink className="w-3 h-3" /> View on Zillow
              </a>
            )}
          </div>
        ) : currentUrl ? (
          <div className="space-y-2">
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center gap-1.5 transition-colors"
              data-testid="zillow-link"
            >
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              {currentUrl.length > 55 ? currentUrl.slice(0, 55) + "..." : currentUrl}
            </a>
            <p className="text-xs text-[var(--t-text-subtle)]">
              Click &ldquo;Update&rdquo; to fetch listing details from this URL.
            </p>
          </div>
        ) : (
          <p className="text-sm text-[var(--t-text-subtle)] italic">
            Paste a Zillow URL to pull in listing details.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
